import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocaleSwitchStore } from 'stores/useLocaleSwitchStore';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import { LAYER } from 'modules/Common/utils/layers';

// App-wide scrim shown while switchLocale() is in flight (see
// modules/Common/utils/switchLocale.js for what it waits on). Mounted once in
// _app.jsx, OUTSIDE the router, so it covers both the signed-in chrome and the
// outside routes.
//
// The label renders in the OUTGOING language while the new resources load,
// then flips to the target language for the refetch wait — both halves are
// readable to the person who initiated the switch, which is the property that
// matters.
//
// Inline styles on purpose, kept from the prebuilt-bundle era: this overlay
// must render even if a CSS chunk failed to load mid-locale-switch, so it
// depends on no stylesheet at all.
export default function LocaleSwitchOverlay() {
  const { t } = useTranslation('common');
  const pendingLocale = useLocaleSwitchStore((s) => s.pendingLocale);

  if (!pendingLocale) return null;

  const label = t('loader.switchingLanguage', 'Switching language');

  return (
    <div
      data-testid="locale-switch-overlay"
      aria-busy="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: LAYER.blockingOverlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Translucent, not solid: the app staying visible underneath is what
        // reads as "reloading" rather than "navigated away".
        background: 'rgba(255, 255, 255, 0.82)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        cursor: 'wait',
      }}
    >
      <CgPageLoader
        minHeight={0}
        statusMessages={[label]}
        ariaLabel={label}
      />
    </div>
  );
}
